import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFieldArray } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { HrActionText, HrFlexContainer, HrGrey, HrPrimary, HrRed } from '@components/styles/hr-styles';
import { Input } from '@components/form/input';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import {
  MAX_CLOSED_FIELD_OPTIONS,
  MIN_CLOSED_FIELD_OPTIONS
} from '@/vacancy_request/components/template-request-form/custom-fields/constants';

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const DragHandle = styled.span`
  color: ${HrGrey};
  font-size: 20px;
  line-height: 40px;
  cursor: grab;
  flex: 0 0 auto;

  &:active {
    cursor: grabbing;
  }
`;

const RemoveOptionButton = styled.button`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${HrRed};
  border-radius: 6px;
  color: ${HrRed};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/**
 * Opciones de un campo cerrado. Sigue el patrón de las opciones de las preguntas de la creación de
 * vacante (client/js/crud_vacancy/components/questionMultiple.js), sin la columna de "descalifica":
 * HR-2485 la excluye explícitamente.
 *
 * Se arranca con `MIN_CLOSED_FIELD_OPTIONS` opciones y no se puede bajar de ahí: el tacho aparece en
 * todas las filas, como en el diseño, y se bloquea al llegar al mínimo. Una pregunta cerrada con una
 * sola opción no es una pregunta.
 */
export const FieldOptionsEditor = ({ control, register, formState, disabled }) => {
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.customFields');
  const { fields, append, remove, move } = useFieldArray({ control, name: 'options' });

  const canRemove = fields.length > MIN_CLOSED_FIELD_OPTIONS;
  const canAppend = fields.length < MAX_CLOSED_FIELD_OPTIONS;

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    move(result.source.index, result.destination.index);
  };

  return (
    <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='12px'>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='customFieldOptions'>
          {(droppableProvided) => (
            <HrFlexContainer
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              flexDirection='column'
              alignItems='flex-start'
              gap='12px'
            >
              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index} isDragDisabled={disabled}>
                  {(draggableProvided) => (
                    <OptionRow ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                      <DragHandle {...draggableProvided.dragHandleProps} className='hr-Move' />
                      <Input
                        name={`options.${index}.value`}
                        type='text'
                        placeholder={t('modal.options.placeholder')}
                        register={register}
                        formState={formState}
                        disabled={disabled}
                        errorPosition='bottom'
                        validate={{ required: true, whiteSpace: true, maxLength: 100 }}
                      />
                      <RemoveOptionButton
                        type='button'
                        onClick={() => remove(index)}
                        disabled={disabled || !canRemove}
                        aria-label={t('modal.options.remove')}
                      >
                        <span className='hr-Trash' />
                      </RemoveOptionButton>
                    </OptionRow>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </HrFlexContainer>
          )}
        </Droppable>
      </DragDropContext>

      {canAppend && !disabled && (
        <HrActionText color={HrPrimary} onClick={() => append({ value: '' })} type='button'>
          {`+ ${t('modal.options.add')}`}
        </HrActionText>
      )}
    </HrFlexContainer>
  );
};

FieldOptionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  formState: PropTypes.object.isRequired,
  disabled: PropTypes.bool
};

FieldOptionsEditor.defaultProps = {
  disabled: false
};
